/**
 * Cards in a grid of three columns, each spanning one, two or three — and,
 * with `onMove`, reordering them by dragging (mouse, touch, keyboard).
 *
 * **It does not fetch, and it does not know what a card is.** It places what
 * `render` returns and raises `onMove(from, to)` when a card is dropped on
 * another; the page decides what that means.
 *
 * - **Columns:** one on a phone, two from `sm`, three from `lg`. A card spans
 *   its width, or all the columns there are when it is wider.
 * - **Order is the order of `items`.** A card that does not fit the rest of a
 *   row starts the next one, and leaves the gap. Filling gaps with later cards
 *   (`dense`) would show the cards in an order that is not the saved one.
 * - **A card that renders nothing takes no place**: its cell is `:empty` and
 *   hidden.
 * - **Cards in one row are as tall as the tallest.** The cell is a flex
 *   column, and the card in it grows to fill it.
 * - **Dragging is by a handle**, which `render` receives and places. A whole
 *   card as the drag target would make Space on any button inside it start a
 *   drag. Built on `@dnd-kit/core`, like `KanbanBoard`; the announcements are
 *   translated in `common:cardGrid.*`.
 */
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';

export interface CardGridItem {
  key: string;
  /** Columns, from 1 to 3. */
  width: 1 | 2 | 3;
  /** The card's name, as text: what a screen reader announces while it moves. */
  label: string;
  /** `handle` is the drag handle to place in the card while `onMove` is set, and null otherwise. */
  render: (handle: ReactNode) => ReactNode;
}

export interface CardGridProps {
  items: readonly CardGridItem[];
  /** Makes the cards draggable. Raised when a card is dropped on another one. */
  onMove?: (fromKey: string, toKey: string) => void;
}

function cellSx(width: 1 | 2 | 3) {
  return {
    gridColumn: { xs: 'span 1', sm: `span ${Math.min(width, 2)}`, lg: `span ${width}` },
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '& > *': { flexGrow: 1 },
    '&:empty': { display: 'none' },
  };
}

const GRID_SX: SxProps<Theme> = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
};

function DraggableCell({ item }: { item: CardGridItem }) {
  const { t } = useTranslation('common');
  const drag = useDraggable({ id: item.key });
  const drop = useDroppable({ id: item.key });
  const transform = drag.transform;

  const handle = (
    <IconButton
      ref={drag.setActivatorNodeRef}
      size="small"
      aria-label={t('cardGrid.handle', { item: item.label })}
      sx={{ cursor: drag.isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      {...drag.attributes}
      {...drag.listeners}
    >
      <DragIndicatorIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Box
      ref={(node: HTMLElement | null) => {
        drag.setNodeRef(node);
        drop.setNodeRef(node);
      }}
      sx={[
        cellSx(item.width),
        {
          position: 'relative',
          zIndex: drag.isDragging ? 2 : 'auto',
          opacity: drag.isDragging ? 0.8 : 1,
          borderRadius: 1,
          outline: drop.isOver && !drag.isDragging ? 2 : 0,
          outlineColor: 'primary.main',
          outlineStyle: 'solid',
          outlineOffset: 2,
        },
      ]}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
    >
      {item.render(handle)}
    </Box>
  );
}

function SortableGrid({ items, onMove }: { items: readonly CardGridItem[]; onMove: (from: string, to: string) => void }) {
  const { t } = useTranslation('common');
  // A short distance before a drag starts, so a click on the handle is still a click.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const labels = useMemo(() => new Map(items.map((item) => [item.key, item.label])), [items]);
  const labelOf = (id: UniqueIdentifier | undefined): string =>
    id === undefined ? '' : (labels.get(String(id)) ?? '');

  const announcements: Announcements = {
    onDragStart: ({ active }) => t('cardGrid.picked', { item: labelOf(active.id) }),
    onDragOver: ({ active, over }) =>
      over && over.id !== active.id
        ? t('cardGrid.over', { item: labelOf(active.id), target: labelOf(over.id) })
        : t('cardGrid.overNone', { item: labelOf(active.id) }),
    onDragEnd: ({ active, over }) =>
      over && over.id !== active.id
        ? t('cardGrid.dropped', { item: labelOf(active.id), target: labelOf(over.id) })
        : t('cardGrid.cancelled', { item: labelOf(active.id) }),
    onDragCancel: ({ active }) => t('cardGrid.cancelled', { item: labelOf(active.id) }),
  };

  function handleEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && over.id !== active.id) onMove(String(active.id), String(over.id));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{ announcements, screenReaderInstructions: { draggable: t('cardGrid.instructions') } }}
      onDragEnd={handleEnd}
    >
      <Box sx={GRID_SX}>
        {items.map((item) => (
          <DraggableCell key={item.key} item={item} />
        ))}
      </Box>
    </DndContext>
  );
}

export function CardGrid({ items, onMove }: CardGridProps) {
  if (onMove) return <SortableGrid items={items} onMove={onMove} />;
  return (
    <Box sx={GRID_SX}>
      {items.map((item) => (
        <Box key={item.key} sx={cellSx(item.width)}>
          {item.render(null)}
        </Box>
      ))}
    </Box>
  );
}
